import { getScrollController } from "@scripts/scrollController";

export const SCROLL_LOCKED = "fa-scroll-locked";

const NATIVE = "[data-fa-scroll-native]";
const FIELDS = "input, textarea, select, [contenteditable]";
const SPACE_ACTIVATES = 'button, summary, [role="button"]';

const KEYS = new Set([
	" ",
	"PageUp",
	"PageDown",
	"Home",
	"End",
	"ArrowUp",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
]);

let holds = 0;
let listeners: AbortController | undefined;
let anchorX = 0;
let anchorY = 0;

/* The same attribute Lenis's own `prevent` reads, so a pane opts out of both with one hook. */
const exempt = (target: EventTarget | null) =>
	target instanceof Element && target.closest(NATIVE) !== null;

const apply = () => {
	const root = document.documentElement;

	anchorX = window.scrollX;
	anchorY = window.scrollY;
	root.classList.add(SCROLL_LOCKED);

	listeners = new AbortController();

	const { signal } = listeners;

	/* Not `overflow: clip`: clipping stops the root being a scroll container, so the scrollbar
	   disappears and the widened box shifts every fixed element by the gutter [STYLE-5]. */
	const block = (event: Event) => {
		if (!exempt(event.target)) event.preventDefault();
	};

	window.addEventListener("wheel", block, { passive: false, signal });

	/* The first touchmove decides who owns the gesture: let one through and iOS hands the drag to
	   the scroller, after which every preventDefault is ignored and the pin below yanks it back. */
	window.addEventListener(
		"touchmove",
		(event) => {
			if (event.touches.length > 1) return;

			block(event);
		},
		{ passive: false, signal },
	);

	window.addEventListener(
		"keydown",
		(event) => {
			if (!KEYS.has(event.key) || exempt(event.target)) return;

			const target = event.target instanceof Element ? event.target : null;

			if (target?.closest(FIELDS)) return;
			if (event.key === " " && target?.closest(SPACE_ACTIVATES)) return;

			event.preventDefault();
		},
		{ passive: false, signal },
	);

	/* Nothing cancels a drag on the native scrollbar thumb, so the position is put back instead. */
	window.addEventListener(
		"scroll",
		() => {
			if (window.scrollX !== anchorX || window.scrollY !== anchorY)
				window.scrollTo(anchorX, anchorY);
		},
		{ signal },
	);

	getScrollController()?.stop();
};

const release = () => {
	document.documentElement.classList.remove(SCROLL_LOCKED);

	listeners?.abort();
	listeners = undefined;

	getScrollController()?.start();
};

export const lockScroll = (): (() => void) => {
	if (holds === 0) apply();
	holds += 1;

	let held = true;

	return () => {
		if (!held) return;

		held = false;
		holds -= 1;

		if (holds === 0) release();
	};
};

export const isScrollLocked = (): boolean => holds > 0;
